<template>
  <article
    class="flex flex-col overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm transition-shadow hover:shadow-md"
    :data-testid="`cbt-test-card-${test.id}`"
  >
    <!-- Title + status -->
    <header class="flex items-start justify-between gap-3 border-b border-gray-100 p-4">
      <div class="min-w-0">
        <h3 class="truncate text-base font-semibold text-gray-900" :title="test.title">{{ test.title }}</h3>
        <p class="mt-0.5 flex items-center gap-1.5 truncate text-xs text-gray-500">
          <i class="pi pi-users text-[11px]"></i>
          {{ test.scope }}
        </p>
      </div>

      <span
        class="inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium"
        :class="statusClasses"
      >
        <i :class="statusIcon" class="text-[10px]"></i>
        {{ statusLabel }}
      </span>
    </header>

    <!-- Facts -->
    <div class="grid grid-cols-2 gap-px bg-gray-100">
      <div class="bg-white p-3">
        <p class="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gray-400">
          <i class="pi pi-question-circle text-[11px]"></i> Questions
        </p>
        <p class="mt-1 text-sm font-semibold text-gray-800">{{ test.questionCount }}</p>
      </div>
      <div class="bg-white p-3">
        <p class="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gray-400">
          <i class="pi pi-clock text-[11px]"></i> Duration
        </p>
        <p class="mt-1 text-sm font-semibold text-gray-800">{{ test.duration }} min</p>
      </div>
      <div class="bg-white p-3">
        <p class="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gray-400">
          <i class="pi pi-calendar text-[11px]"></i> Opens
        </p>
        <p class="mt-1 text-sm text-gray-700">{{ test.startLabel }}</p>
      </div>
      <div class="bg-white p-3">
        <p class="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-gray-400">
          <i class="pi pi-flag text-[11px]"></i> Closes
        </p>
        <p class="mt-1 text-sm text-gray-700">{{ test.endLabel }}</p>
      </div>
    </div>

    <!-- Action -->
    <footer class="mt-auto border-t border-gray-100 p-4">
      <Button
        v-if="test.status !== 'ended'"
        :label="actionLabel"
        :icon="test.status === 'live' ? 'pi pi-play' : 'pi pi-hourglass'"
        class="w-full"
        :disabled="test.status !== 'live'"
        :data-testid="test.status === 'live' ? 'btn-confirm-start-exam' : 'btn-start-exam-disabled'"
        @click="emit('start', test)"
      />
      <Button
        v-else
        label="Test Closed"
        icon="pi pi-check-circle"
        class="w-full"
        severity="secondary"
        outlined
        disabled
      />
    </footer>
  </article>
</template>

<script setup lang="ts">
export interface CbtTestCardModel {
  id: string;
  title: string;
  scope: string;
  description?: string | null;
  duration: number;
  questionCount: number;
  startLabel: string;
  endLabel: string;
  status: 'live' | 'upcoming' | 'ended';
  /** Milliseconds until the test opens; only meaningful when status is 'upcoming'. */
  startsInMs: number;
}

const props = defineProps<{ test: CbtTestCardModel }>();

const emit = defineEmits<{ start: [test: CbtTestCardModel] }>();

const statusLabel = computed(() => {
  if (props.test.status === 'live') return 'Open now';
  if (props.test.status === 'ended') return 'Closed';
  return 'Upcoming';
});

const statusIcon = computed(() => {
  if (props.test.status === 'live') return 'pi pi-circle-fill';
  if (props.test.status === 'ended') return 'pi pi-lock';
  return 'pi pi-clock';
});

const statusClasses = computed(() => {
  if (props.test.status === 'live') return 'bg-primary text-white';
  if (props.test.status === 'ended') return 'bg-gray-100 text-gray-500';
  return 'bg-primary/10 text-primary';
});

/** "2d 3h", "3h 12m", "12m", "under a minute" */
const countdown = computed(() => {
  const ms = props.test.startsInMs;
  if (ms <= 0) return '';
  const minutes = Math.floor(ms / 60000);
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${mins}m`;
  if (mins > 0) return `${mins}m`;
  return 'under a minute';
});

const actionLabel = computed(() =>
  props.test.status === 'live' ? 'Start Exam' : `Starts in ${countdown.value}`,
);
</script>
